import { Routes, Route, Navigate } from 'react-router-dom'
import { ToastProvider, Toaster } from './components/atoms/Toaster'
import { AuthProvider, useAuth } from './store/auth.store'
import { ThemeProvider } from './store/theme.store'
import { ErrorBoundary } from './components/common/ErrorBoundary'
import { Sidebar } from './components/organisms/Sidebar'
import { Header } from './components/organisms/Header'
import { LoginPage } from './components/pages/LoginPage'
import { ForgotPasswordPage } from './components/pages/ForgotPasswordPage'
import { ResetPasswordPage } from './components/pages/ResetPasswordPage'
import { DashboardPage } from './components/pages/DashboardPage'
import { EmailsPage } from './components/pages/EmailsPage'
import { CasesPage } from './components/pages/CasesPage'
import { AlertsPage } from './components/pages/AlertsPage'
import { AlertDetailPage } from './components/pages/AlertDetailPage'
import { SettingsPage } from './components/pages/SettingsPage'
import { EmailDetailPage } from './components/pages/EmailDetailPage'
import { CaseDetailPage } from './components/pages/CaseDetailPage'
import { AnalyticsPage } from './components/pages/AnalyticsPage'
import { RegulatoryCalendarPage } from './components/pages/RegulatoryCalendarPage'
import { ComposeModalProvider } from './components/organisms/ComposeModal'
import { useCompany } from './hooks/useCompany'
import { useNotificationToasts } from './hooks/useNotificationToasts'
import { useEffect } from 'react'

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth()
  const { data: company } = useCompany()
  useNotificationToasts()

  useEffect(() => {
    if (company?.name) {
      document.title = `${company.name} - ComCenter`
    } else {
      document.title = 'ComCenter'
    }
  }, [company?.name])

  if (isLoading) {
    return <div className="flex items-center justify-center h-screen bg-gray-50 dark:bg-gray-950 text-gray-900 dark:text-gray-100">Cargando...</div>
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return (
    <div className="h-screen flex overflow-hidden bg-gray-50 dark:bg-gray-950 transition-colors">
      <Sidebar />
      <div className="flex-1 flex flex-col h-full min-w-0">
        <Header />
        <main className="flex-1 min-h-0 overflow-y-auto p-4 md:p-6">
          <ErrorBoundary>
            {children}
          </ErrorBoundary>
        </main>
      </div>
    </div>
  )
}

function AppRoutes() {

  return (
    <Routes>
      {/* Rutas públicas */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      
      {/* Rutas protegidas */}
      <Route path="/" element={
        <ProtectedRoute>
          <DashboardPage />
        </ProtectedRoute>
      } />
      
      <Route path="/emails" element={
        <ProtectedRoute>
          <EmailsPage />
        </ProtectedRoute>
      } />

      <Route path="/analytics" element={
        <ProtectedRoute>
          <AnalyticsPage />
        </ProtectedRoute>
      } />

      <Route path="/calendar" element={
        <ProtectedRoute>
          <RegulatoryCalendarPage />
        </ProtectedRoute>
      } />

      <Route path="/reports" element={
        <ProtectedRoute>
          <AnalyticsPage />
        </ProtectedRoute>
      } />
      
      <Route path="/emails/:id" element={
        <ProtectedRoute>
          <EmailDetailPage />
        </ProtectedRoute>
      } />
      
      <Route path="/cases" element={
        <ProtectedRoute>
          <CasesPage />
        </ProtectedRoute>
      } />
      
      <Route path="/cases/:id" element={
        <ProtectedRoute>
          <CaseDetailPage />
        </ProtectedRoute>
      } />
      
      <Route path="/alerts" element={
        <ProtectedRoute>
          <AlertsPage />
        </ProtectedRoute>
      } />

      <Route path="/alerts/:id" element={
        <ProtectedRoute>
          <AlertDetailPage />
        </ProtectedRoute>
      } />
      
      <Route path="/settings" element={
        <ProtectedRoute>
          <SettingsPage />
        </ProtectedRoute>
      } />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

export default function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <ToastProvider>
            <ComposeModalProvider>
              <AppRoutes />
            </ComposeModalProvider>
            <Toaster />
          </ToastProvider>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  )
}
